import { useTranslation } from "react-i18next";
import { FC, Props } from "./typings";
import {
  ColumnsPanelTrigger,
  gridColumnLookupSelector,
  gridColumnReorderDragColSelector,
  gridRowGroupingSanitizedModelSelector,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Fragment, Ref, useEffect, useRef, useState } from "react";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React from "react";
import { IGridFilter } from "dto/IGridFilter";
import styles from "./styles.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import FilterIcon from "assets/icons/tune.svg?react";
import CloseIcon from "assets/icons/close.svg?react";
import HollowButton from "components/HollowButton";
import SelectFlagNew from "components/SelectFlagNew";
import SearchLabel from "components/SearchLabel";
import { ITag } from "dto/ITag";
import { IStatus } from "dto/IStatus";
import SearchMemberNew from "components/SearchMemberNew";
import RangePicker from "components/RangePicker";
import Input from "components/Input";
import Button from "components/Button";
import Popper from "components/Popper";
import classNames from "classnames";
import BinIcon from "assets/icons/bin.svg?react";
import EditIcon from "assets/icons/mode_edit.svg?react";
import { IWorkspaceMember } from "dto/IWorkspace";

const CustomToolbar: FC<Props> = ({
  listName,
  leftChildren,
  rightChildren,
  rightChildren2,
  editFilter,
  setEditFilter,
  filterCriteria,
  filterAsSlide = true,
  listType,
  statusId,
  setStatusId,
  tagType,
  tagId,
  setTagId,
  dateFilterLabel,
  userId,
  setUserId,
  showColumnGrouping = true,
  showSearch = false,
  searchText,
  setSearchText,
}) => {
  const { t } = useTranslation();
  const STORAGE_FILTERS_KEY = `${listName}_gridFilters`;

  const apiRef = useGridApiContext();
  const rowGroupingModel = useGridSelector(
    apiRef,
    gridRowGroupingSanitizedModelSelector
  );
  const columnsLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const draggedColumn = useGridSelector(
    apiRef,
    gridColumnReorderDragColSelector
  );
  const [draggedChip, setDraggedChip] = useState<string | null>(null);

  const [showFiltersModal, setShowFiltersModal] = useState<boolean>(false);

  const [filters, setFilters] = useState<IGridFilter[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<IGridFilter>(null);
  const [showFilterName, setShowFilterName] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");

  const saveGridFilters = (filters: IGridFilter[]) => {
    localStorage.setItem(STORAGE_FILTERS_KEY, JSON.stringify(filters));
  };

  const loadGridFilters = () => {
    const data = localStorage.getItem(STORAGE_FILTERS_KEY);
    try {
      return data ? (JSON.parse(data) as IGridFilter[]) : [];
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    const loadedFilters = loadGridFilters();
    setFilters(loadedFilters);
  }, []);

  const updateFilters = (newFilters: IGridFilter[]) => {
    setFilters(newFilters);
    saveGridFilters(newFilters);
  };

  const handleToolbarDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (
      draggedColumn &&
      !rowGroupingModel.includes(draggedColumn) &&
      columnsLookup[draggedColumn].groupable
    ) {
      apiRef.current.addRowGroupingCriteria(draggedColumn);
    }
  };

  const handleToolbarDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    // if (
    //   draggedColumn &&
    //   event.currentTarget &&
    //   !event.currentTarget.contains(event.relatedTarget as Node)
    // ) {
    //   console.log("removing grouping", event.currentTarget);
    //   apiRef.current.removeRowGroupingCriteria(draggedColumn);
    // }
  };

  const handleChipDragStart =
    (field: string) => (event: React.DragEvent<HTMLDivElement>) => {
      setDraggedChip(field);
      event.dataTransfer.effectAllowed = "move";
    };

  const handleChipDragEnd = () => {
    setDraggedChip(null);
  };

  const handleChipDragOver =
    (targetField: string) => (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const draggedField = draggedChip || draggedColumn;
      if (!draggedField || draggedField === targetField) {
        return;
      }

      const currentIndex = rowGroupingModel.indexOf(draggedField);
      const targetIndex = rowGroupingModel.indexOf(targetField);

      if (currentIndex === -1 || targetIndex === -1) {
        return;
      }

      if (currentIndex !== targetIndex) {
        const newModel = [...rowGroupingModel];
        newModel.splice(currentIndex, 1);
        newModel.splice(targetIndex, 0, draggedField);
        apiRef.current.setRowGroupingModel(newModel);
      }
    };

  const moveRowGroup = (field: string, position: number) => {
    if (position < 0 || position > rowGroupingModel.length) {
      return;
    }

    const currentIndex = rowGroupingModel.indexOf(field);
    const newModel = [...rowGroupingModel];
    newModel.splice(currentIndex, 1);
    newModel.splice(position, 0, field);
    apiRef.current.setRowGroupingModel(newModel);
  };

  const removeRowGroup = (field: string) => {
    if (columnsLookup[field].groupable) {
      apiRef.current.removeRowGroupingCriteria(field);
    }
  };

  const setFilter = (filter: IGridFilter) => {
    setSelectedFilter(filter);
    setEditFilter(filter);

    setFilterName(filter.Name);
    setStatusId(filter.StatusId);
    setTagId(filter.TagId);
  };

  const handleStatusChange = (status?: IStatus) => {
    console.log("Handle status change", status);
    setStatusId(status?.Id);
    if (editFilter) {
      setEditFilter({ ...editFilter, StatusId: status?.Id });
    }
  };

  const handleTagChange = (tag?: ITag) => {
    setTagId(tag?.Id);

    if (editFilter) {
      setEditFilter({ ...editFilter, TagId: tag?.Id });
    }
  };

  const handleSave = () => {
    if (editFilter.Id === 0 && !showFilterName) {
      setShowFilterName(true);
    } else {
      let newF = filters;
      if (editFilter.Id === 0) {
        editFilter.Id = Math.max(...filters.map((x) => x.Id), 0) + 1;
      }

      editFilter.Name = filterName ?? t("toolbar.newFilter");
      newF = [...filters.filter((x) => x.Id !== editFilter.Id), editFilter];
      updateFilters(newF);
      setEditFilter(null);
    }
  };

  const handleCancel = () => {
    setShowFilterName(false);
  };

  const handleEdit = (filter: IGridFilter) => {
    setEditFilter(filter);
  };

  const handleRemoveFilter = (filter: IGridFilter) => {
    const newF = [...filters.filter((x) => x.Id !== filter.Id)];
    updateFilters(newF);
  };

  const handleUserChange = (member: IWorkspaceMember | null) => {
    if (setUserId) {
      setUserId(member?.UserId);
    }
  };

  const handleClearFilter = () => {
    setEditFilter(null);
    setSelectedFilter(null);

    setStatusId(null);
    setTagId(null);
    setUserId(null);
  };

  const filtersRef = useRef(null);
  const inputWrapper = useRef(null);

  return (
    <Toolbar>
      <div className={styles.left}>
        <div>{leftChildren}</div>
        <div
          className={styles.grouping}
          onDragOver={handleToolbarDragOver}
          onDragLeave={handleToolbarDragLeave}
        >
          {showColumnGrouping && (
            <MoveUpIcon fontSize="small" color="action" sx={{ mr: 0.75 }} />
          )}
          {showColumnGrouping && (
            <Box sx={{ flexGrow: 1 }}>
              {rowGroupingModel.length > 0 ? (
                <Fragment>
                  {rowGroupingModel.map((field, index) => {
                    const isDraggedField =
                      draggedChip === field || draggedColumn === field;
                    const isGroupable = columnsLookup[field].groupable;
                    const label = columnsLookup[field].headerName ?? field;

                    return (
                      <Fragment key={field}>
                        {index > 0 && (
                          <ChevronRightIcon fontSize="small" color="action" />
                        )}
                        <ToolbarButton
                          id={field}
                          render={({
                            children: _children, // eslint-disable-line @typescript-eslint/no-unused-vars
                            color: _color, // eslint-disable-line @typescript-eslint/no-unused-vars
                            size: _size, // eslint-disable-line @typescript-eslint/no-unused-vars
                            ref,
                            onKeyDown,
                            ...chipProps
                          }) => {
                            const handleKeyDown = (
                              event: React.KeyboardEvent<HTMLDivElement>
                            ) => {
                              if (
                                event.key === "ArrowRight" &&
                                event.shiftKey
                              ) {
                                moveRowGroup(field, index + 1);
                              } else if (
                                event.key === "ArrowLeft" &&
                                event.shiftKey
                              ) {
                                moveRowGroup(field, index - 1);
                              } else {
                                onKeyDown?.(event);
                              }
                            };

                            return (
                              <Chip
                                {...chipProps}
                                ref={ref as Ref<HTMLDivElement>}
                                label={label}
                                sx={{
                                  cursor: "grab",
                                  opacity: isDraggedField ? 0.5 : 1,
                                }}
                                onDelete={() => removeRowGroup(field)}
                                deleteIcon={!isGroupable ? <span /> : undefined}
                                onKeyDown={handleKeyDown}
                                onDragStart={handleChipDragStart(field)}
                                onDragEnd={handleChipDragEnd}
                                onDragOver={handleChipDragOver(field)}
                                draggable
                              />
                            );
                          }}
                        />
                      </Fragment>
                    );
                  })}
                </Fragment>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ flex: 1 }}
                  noWrap
                >
                  {t("toolbar.dragToGroup")}
                </Typography>
              )}
            </Box>
          )}
        </div>
      </div>

      {!showColumnGrouping && <Box sx={{ flexGrow: 1 }} />}

      <div className={styles.right}>
        <div className={styles.row}>
          {showSearch && (
            <div
              onClick={() => {
                //@ts-ignore
                inputWrapper?.current?.focus?.();
              }}
              className={classNames(styles.searchWrapper)}
            >
              <div className={styles.icon2}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <div className={styles.inputWrapper}>
                <input
                  id="toolbar_search"
                  ref={inputWrapper}
                  className={styles.input}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t("common.search")}
                />
              </div>

              <div className={styles.cleanIcon}>
                {searchText?.length > 0 && (
                  <CloseIcon
                    onClick={() => {
                      setSearchText("");
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.row}>
          {rightChildren}

          {!editFilter && (
            <div className={styles.type}>
              <div
                onClick={() => setShowFiltersModal(!showFiltersModal)}
                className={styles.typeText}
              >
                {t("toolbar.yourFilters")}
                <FontAwesomeIcon
                  icon={showFiltersModal ? faChevronUp : faChevronDown}
                />
              </div>
              {showFiltersModal && (
                <div className={styles.dropdown}>
                  {filters.map((x) => (
                    <div
                      onClick={() => {
                        setFilter(x);
                        setShowFiltersModal(false);
                      }}
                      className={styles.box}
                    >
                      <div className={styles.name}>{x.Name}</div>
                      <div className={styles.tools}>
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEdit(x);
                          }}
                          className={styles.menuItem}
                        >
                          <EditIcon title={t("common.edit")} />
                        </div>
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveFilter(x);
                          }}
                          className={styles.menuItem}
                        >
                          <BinIcon title={t("common.delete")} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {filters.length === 0 && (
                    <div className={styles.empty}>{t("toolbar.noFilters")}</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div ref={filtersRef}>
            {!editFilter && (
              <Tooltip title={t("toolbar.filters")}>
                <div
                  className={classNames(
                    styles.icon,
                    selectedFilter || editFilter || statusId || tagId
                      ? styles.active
                      : undefined
                  )}
                  onClick={() => {
                    if (selectedFilter) {
                      setEditFilter(selectedFilter);
                    } else {
                      setEditFilter({
                        Id: 0,
                        ListType: listType,
                        Name: t("toolbar.newFilter"),
                      });
                    }
                  }}
                >
                  <FilterIcon></FilterIcon>
                </div>
              </Tooltip>
            )}
          </div>

          {editFilter && !filterAsSlide && (
            <Popper
              anchorEl={filtersRef?.current}
              open={editFilter !== null}
              setOpen={() => setEditFilter(null)}
              placement="left-start"
              closeOnClickAway={false}
            >
              <div className={styles.editFilter}>
                {showFilterName && (
                  <div className={styles.row}>
                    <Input
                      value={filterName}
                      onChange={setFilterName}
                      label={t("toolbar.filterName")}
                    ></Input>
                  </div>
                )}
                <div className={styles.row}>
                  <SelectFlagNew
                    selectedStatus={null}
                    onChoose={(status) => handleStatusChange(status)}
                    allowSearch={false}
                    showButtons={false}
                    showCounter={true}
                    listType={listType}
                    selectedStatusId={statusId}
                    showChoosen={true}
                  />

                  <SearchLabel
                    label={t("columns.label")}
                    type={tagType}
                    onChoose={handleTagChange}
                    allowSearch={false}
                    showButtons={false}
                    showCounter={true}
                    selectedTagId={tagId}
                  />
                  <SearchMemberNew
                    onChoose={handleUserChange}
                    selectedUserId={userId}
                    label={t("toolbar.assignee")}
                    allowSearch={false}
                    showButtons={false}
                    // showCounter={true}
                  ></SearchMemberNew>
                  <RangePicker
                    showAllDay={false}
                    label={dateFilterLabel}
                    onAccept={() => {}}
                  ></RangePicker>
                  {filterCriteria}
                  {!showFilterName && (
                    <HollowButton
                      className={styles.saveButton}
                      text={t("common.save")}
                      onClick={() => handleSave()}
                    ></HollowButton>
                  )}
                  {!showFilterName && (
                    <div
                      className={styles.closeIcon}
                      onClick={() => handleClearFilter()}
                    >
                      <CloseIcon></CloseIcon>
                    </div>
                  )}
                </div>
                {showFilterName && (
                  <div className={styles.buttons}>
                    <HollowButton
                      text={t("common.cancel")}
                      onClick={() => handleCancel()}
                    ></HollowButton>

                    <Button text={t("common.save")} onClick={() => handleSave()}></Button>
                  </div>
                )}
              </div>
            </Popper>
          )}

          {editFilter && filterAsSlide && (
            <div
              className={styles.filterSlideOverlay}
              onClick={(e) => {
                if (e.target === e.currentTarget) setEditFilter(null);
              }}
            >
              <div className={styles.filterSlidePanel}>
                <div className={styles.filterSlideHeader}>
                  <span className={styles.filterSlideTitle}>
                    {t("toolbar.filters")}
                  </span>
                  <div
                    className={styles.closeIcon}
                    onClick={() => handleClearFilter()}
                  >
                    <CloseIcon></CloseIcon>
                  </div>
                </div>

                <div className={styles.filterSlideBody}>
                  {showFilterName && (
                    <Input
                      value={filterName}
                      onChange={setFilterName}
                      label={t("toolbar.filterName")}
                    ></Input>
                  )}
                  <SelectFlagNew
                    selectedStatus={null}
                    onChoose={(status) => handleStatusChange(status)}
                    allowSearch={false}
                    showButtons={false}
                    showCounter={true}
                    listType={listType}
                    selectedStatusId={statusId}
                    showChoosen={true}
                  />
                  <SearchLabel
                    label={t("columns.label")}
                    type={tagType}
                    onChoose={handleTagChange}
                    allowSearch={false}
                    showButtons={false}
                    showCounter={true}
                    selectedTagId={tagId}
                  />
                  <SearchMemberNew
                    onChoose={handleUserChange}
                    selectedUserId={userId}
                    label={t("toolbar.assignee")}
                    allowSearch={false}
                    showButtons={false}
                  ></SearchMemberNew>
                  <RangePicker
                    showAllDay={false}
                    label={dateFilterLabel}
                    onAccept={() => {}}
                  ></RangePicker>
                  <div className={styles.filterSlideCriteria}>
                    {filterCriteria}
                  </div>
                </div>

                <div className={styles.filterSlideFooter}>
                  <HollowButton
                    text={t("common.save")}
                    onClick={() => handleSave()}
                  ></HollowButton>
                </div>
              </div>
            </div>
          )}

          <Tooltip title={t("toolbar.columns")}>
            <ColumnsPanelTrigger render={<ToolbarButton />}>
              <ViewColumnIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>

          {/* <Tooltip title="Pivot">
            <PivotPanelTrigger
              render={(triggerProps, state) => (
                <ToolbarButton
                  {...triggerProps}
                  color={state.active ? "primary" : "default"}
                />
              )}
            >
              <PivotTableChartIcon fontSize="small" />
            </PivotPanelTrigger>
          </Tooltip> */}
          {rightChildren2}
        </div>
      </div>
    </Toolbar>
  );
};

export default CustomToolbar;
